import { useMemo, useState, type ComponentType } from 'react';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import type { UserBrandInsight } from '../types';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../utils/help/help-content';
import { brandsColumnConfig, type BrandRow } from '../config/brandsColumnConfig';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
  hideExportHelpAlert?: boolean;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

interface BrandsTableProps {
  brands: UserBrandInsight[];
  projectId?: string;
  onAddCompetitor?: (brand: UserBrandInsight) => Promise<void>;
  debug?: boolean;
  onDebugChange?: (show: boolean) => void;
  showHeader?: boolean;
  showDebugToggle?: boolean;
  compact?: boolean;
  rowHeight?: number;
  headerHeight?: number;
}

export function BrandsTable({
  brands,
  projectId,
  onAddCompetitor,
  debug = false,
  onDebugChange,
  showHeader = true,
  showDebugToggle = true,
  compact = false,
  rowHeight,
  headerHeight,
}: BrandsTableProps) {
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [uncontrolledDebug, setUncontrolledDebug] = useState(debug);
  const showDebug = onDebugChange ? debug : uncontrolledDebug;
  const setShowDebug = (next: boolean) => {
    onDebugChange?.(next);
    if (!onDebugChange) setUncontrolledDebug(next);
  };

  const showActions = !!projectId && !!onAddCompetitor;

  const rows: BrandRow[] = useMemo(
    () =>
      brands.map((b, i) => ({
        ...b,
        _rowIndex: i,
        _rowKey: `${b.brandName}-${b.position}-${i}`,
      })),
    [brands],
  );

  async function handleAdd(row: BrandRow, key: string) {
    if (!onAddCompetitor) return;
    setAddingKey(key);
    try {
      await onAddCompetitor(row);
    } finally {
      setAddingKey(null);
    }
  }

  const columns = useMemo(
    () =>
      brandsColumnConfig({
        showDebug,
        showActions,
        addingKey,
        compact,
        onAdd: (row, key) => void handleAdd(row, key),
      }),
    [showDebug, showActions, addingKey, compact],
  );

  if (!brands.length) return null;

  const showToolbar = showHeader || showDebugToggle;

  return (
    <div className="d-flex flex-column gap-3">
      {showToolbar ? (
        <div
          className={`d-flex align-items-center gap-3 ${
            showHeader ? 'justify-content-between' : 'justify-content-start'
          }`}
        >
          {showHeader ? (
            <p className="fs--1 fw-medium text-500 mb-0">
              <span className="d-inline-flex align-items-center gap-1">
                برندهای شناسایی‌شده
                <HelpTooltip html={getHelpHtml('detected_brands')} />
              </span>
            </p>
          ) : null}
          {showDebugToggle ? (
            <button
              type="button"
              onClick={() => setShowDebug(!showDebug)}
              className="btn btn-link p-0 fs--1 text-primary text-decoration-none"
            >
              {showDebug ? 'مخفی کردن دیباگ' : 'نمایش دیباگ'}
            </button>
          ) : null}
        </div>
      ) : null}
      <TypedAgGridWrapper
        table_name="tracker_ai_brands"
        height="320px"
        columns={columns}
        data={rows}
        total={rows.length}
        hideExportHelpAlert
        settings={{ pagination: false, fullscreen: false, sideBar: false, export: { enabled: false } }}
        otherOptions={
          rowHeight != null || headerHeight != null
            ? {
                ...(rowHeight != null ? { rowHeight } : {}),
                ...(headerHeight != null ? { headerHeight } : {}),
              }
            : undefined
        }
      />
    </div>
  );
}
